import React from 'react';
import { TargetIcon, VoteIcon, ShieldOffIcon, MicOffIcon } from 'lucide-react';
export function ProblemSection() {
  return <section className="section-dramatic py-20 px-4 border-y border-navy-800" data-id="element-290">
      {/* Background Atmosphere */}
      <div className="absolute inset-0 bg-flag-overlay opacity-50 z-0 pointer-events-none" data-id="element-291"></div>
      <div className="absolute inset-0 bg-flag-stripes z-0 pointer-events-none" data-id="element-292"></div>

      {/* Crowd Silhouette */}
      <div className="crowd-bottom opacity-40" data-id="element-293"></div>

      <div className="max-w-5xl mx-auto space-y-20 relative z-10" data-id="element-294">
        {/* The Problem */}
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-295">
          <div className="space-y-6 order-2 md:order-1" data-id="element-296">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight text-shadow-sm" data-id="element-297">
              The collapse of <br data-id="element-298" />
              civilized dialogue
            </h2>
            <div className="w-20 h-1.5 bg-red-gradient rounded-full" data-id="element-299"></div>
            <p className="text-xl text-slate-300 leading-relaxed" data-id="element-300">
              We are witnessing the collapse of civilized dialogue. Today,
              defending family, faith, and freedom of speech doesn't make you a
              citizen with different ideas — it makes you a social{' '}
              <span className="text-white font-bold bg-patriot-red/80 px-2 py-1 rounded shadow-md" data-id="element-301">
                "target"
              </span>
              .
            </p>
            <p className="text-xl text-slate-300 leading-relaxed" data-id="element-302">
              The problem isn't just the external attack; it's the{' '}
              <strong className="text-white border-b border-patriot-red" data-id="element-303">
                Pedagogy of Fear
              </strong>
              . We've been trained to self-censor, to doubt our own convictions,
              and to accept that truth is relative.
            </p>
            <div className="p-6 glass-card rounded-xl border-l-4 border-l-patriot-red relative overflow-hidden shadow-xl" data-id="element-304">
              <div className="absolute top-0 right-0 w-32 h-32 bg-patriot-red/10 blur-3xl rounded-full" data-id="element-305"></div>
              <p className="text-lg text-slate-200 italic relative z-10 font-serif" data-id="element-306">
                "While the elites decide what counts as 'misinformation,' the
                voice of the common citizen is buried under layers of software
                and hate speech laws. The Charlie Kirk case is the darkest
                mirror of our era: if they can try to erase those with millions
                of followers, what chance do you think you have when they decide
                your opinion is no longer permitted?"
              </p>
            </div>
          </div>
          <div className="order-1 md:order-2 flex justify-center" data-id="element-307">
            <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full border border-patriot-red/30 flex items-center justify-center bg-navy-900/50 shadow-[0_0_60px_rgba(178,34,52,0.15)] backdrop-blur-sm" data-id="element-308">
              <div className="absolute inset-0 rounded-full border-2 border-patriot-red/20 animate-spin-slow" data-id="element-309"></div>
              <div className="absolute inset-4 rounded-full border border-patriot-blue/40 animate-spin-reverse-slow" data-id="element-310"></div>
              <div className="absolute inset-12 rounded-full border border-white/10 animate-spin-slow" data-id="element-311"></div>
              <TargetIcon className="w-28 h-28 text-patriot-red/80 drop-shadow-[0_0_20px_rgba(178,34,52,0.6)]" strokeWidth={1} data-id="element-312" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-transparent rounded-full" data-id="element-313"></div>
            </div>
          </div>
        </div>

        {/* Why what you tried didn't work */}
        <div className="space-y-12 pt-10" data-id="element-314">
          <div className="text-center space-y-4" data-id="element-315">
            <h2 className="text-4xl md:text-5xl font-black text-white text-shadow-sm" data-id="element-316">
              Why Everything You've Tried Has Failed
            </h2>
            <p className="inline-block bg-navy-900 text-patriot-red font-mono uppercase tracking-widest text-sm px-4 py-1 rounded-full border border-patriot-red/30" data-id="element-317">
              Systemic failure analysis
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-318">
            <div className="glass-card p-8 rounded-2xl hover:border-patriot-red/50 transition-all duration-500 group hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(178,34,52,0.15)] bg-navy-900/60" data-id="element-319">
              <div className="w-16 h-16 rounded-2xl bg-navy-950 border border-patriot-red/40 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-inner" data-id="element-320">
                <VoteIcon className="w-8 h-8 text-patriot-red" data-id="element-321" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 font-serif" data-id="element-322">
                Voting every four years is not enough
              </h3>
              <p className="text-slate-300 leading-relaxed text-lg" data-id="element-323">
                Real change doesn't happen at the ballot box if the culture has
                already been infected in classrooms and entertainment.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl hover:border-patriot-red/50 transition-all duration-500 group hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(178,34,52,0.15)] bg-navy-900/60" data-id="element-324">
              <div className="w-16 h-16 rounded-2xl bg-navy-950 border border-patriot-red/40 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-inner" data-id="element-325">
                <ShieldOffIcon className="w-8 h-8 text-patriot-red" data-id="element-326" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 font-serif" data-id="element-327">
                Silence is not protection
              </h3>
              <p className="text-slate-300 leading-relaxed text-lg" data-id="element-328">
                Believing that "if I stay out of politics, nothing will happen
                to me" is a dangerous fantasy; the system will eventually come
                for everything you believe in.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl hover:border-patriot-red/50 transition-all duration-500 group hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(178,34,52,0.15)] bg-navy-900/60" data-id="element-329">
              <div className="w-16 h-16 rounded-2xl bg-navy-950 border border-patriot-red/40 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-inner" data-id="element-330">
                <MicOffIcon className="w-8 h-8 text-patriot-red" data-id="element-331" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 font-serif" data-id="element-332">
                Debating by their rules means losing
              </h3>
              <p className="text-slate-300 leading-relaxed text-lg" data-id="element-333">
                You can't win a game where your opponent has the power to turn
                off your microphone at any moment. You need a new strategy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}